'use client';

import { c as _c } from "react/compiler-runtime";
import Script from 'next/script';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useShallow } from 'zustand/react/shallow';
import { ACCOUNT_ID, ENV, GA_ID, GTM_ID, IS_PRODUCT, PRODUCT_DOMAIN, SUB_DOMAIN } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { flushNaverWcsCommands } from '@/utils/analytics/naver-cts';
import { KbAppController, KbHistoryStack } from './kb-pay';
import { AppController, AppModal } from './wello-app';

/**
 * 구글 애널리틱스 설치 코드
 * https://support.google.com/sa360/answer/7566546?hl=ko
 *
 * 아래에 따르면 gtag는 구글 태그 관리자와 함께 사용할 때 필요하지 않음
 * 그러나 현재 gtag() 호출하는 곳이 있어 그냥 둠
 * https://support.google.com/tagmanager/answer/7582054?hl=en
 */
const GA_SCRIPT = {
  __html: `
window.dataLayer = window.dataLayer || [];
function gtag() {
  dataLayer.push(arguments);
}
gtag("js", new Date());
gtag("config", "${GA_ID}");
`
};

/**
 * 구글 태그 관리자 설치 코드
 */
const GTM_SCRIPT = {
  __html: `
(function (w, d, s, l, i) {
  w[l] = w[l] || [];
  w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  var f = d.getElementsByTagName(s)[0],
    j = d.createElement(s),
    dl = l != "dataLayer" ? "&l=" + l : "";
  j.async = true;
  j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
  f.parentNode.insertBefore(j, f);
})(window, document, "script", "dataLayer", "${GTM_ID}");
`
};
const ClientScript = () => {
  const $ = _c(6);
  const webviewType_0 = useDeviceInfoStore(_temp);
  const [isLogin, memberCode] = useAuthStore(useShallow(_temp2));
  const isMounted = isLogin !== undefined;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      [WEBVIEW_TYPE.KB_PAY]: "KB_PAY_APP",
      [WEBVIEW_TYPE.WELLO_APP]: "WELLO_APP",
      [WEBVIEW_TYPE.TOSS]: "TOSS_APP",
      web: "WELLO_WEB"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = `
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ memberCode: '${memberCode}' });
    window.dataLayer.push({ userEnv: '${t0[webviewType_0 ?? "web"]}' });
    `;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      __html: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const gaDataLayerScript = t2;
  let t3;
  if ($[3] !== gaDataLayerScript || $[4] !== isMounted) {
    t3 = isMounted ? <><Script dangerouslySetInnerHTML={gaDataLayerScript} id="DATA_LAYER" /><Script dangerouslySetInnerHTML={GA_SCRIPT} id="GA" /></> : null;
    $[3] = gaDataLayerScript;
    $[4] = isMounted;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const GA_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
export const GlobalScript = () => {
  const $ = _c(9);
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Script async={true} src={GA_SRC} />;
    t1 = <Script dangerouslySetInnerHTML={GTM_SCRIPT} id="GTM" strategy="afterInteractive" />;
    t2 = <Script src="https://t1.kakaocdn.net/kakao_js_sdk/2.7.5/kakao.min.js" integrity="sha384-dok87au0gKqJdxs7msEdBPNnKSRT+/mhTVzq+qOhcL464zXwvcrpjeWvyj1kCdq6" crossOrigin="anonymous" strategy="lazyOnload" onLoad={_temp3} />;
    t3 = IS_PRODUCT ? <Script id="META_PIXEL_CODE" strategy="afterInteractive">{"\n        !function(f,b,e,v,n,t,s)\n        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?\n        n.callMethod.apply(n,arguments):n.queue.push(arguments)};\n        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';\n        n.queue=[];t=b.createElement(e);t.async=!0;\n        t.src=v;s=b.getElementsByTagName(e)[0];\n        s.parentNode.insertBefore(t,s)}(window, document,'script',\n        'https://connect.facebook.net/en_US/fbevents.js');\n\n        // Check if pixel is already initialized\n        if (!window.fbq.getState || !window.fbq.getState().pixels ||\n            !window.fbq.getState().pixels.find(pixel => pixel.id === '846203437117752')) {\n          fbq('init', '846203437117752');\n        }\n        fbq('track', 'PageView');\n      "}</Script> : null;
    t4 = <Script id="boraware-protect-id" strategy="lazyOnload">{"var protect_id = 'i559';"}</Script>;
    t5 = <Script src="//script.boraware.kr/protect_script_v2.js" async={true} strategy="lazyOnload" />;
    t6 = <Script id="smlog-init" strategy="lazyOnload">{"\n        var hpt_info={'_account':'UHPT-32797', '_server': 'a29'};\n      "}</Script>;
    t7 = <Script id="smlog-core" src="//cdn.smlog.co.kr/core/smart.js" strategy="lazyOnload" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <>{t0}{t1}{t2}{t3}{t4}{t5}{t6}{t7}<noscript><img src="//a29.smlog.co.kr/smart_bda.php?_account=32797" style={{
          display: "none",
          width: 0,
          height: 0,
          border: 0
        }} alt="" /></noscript><Script src="//wcs.naver.net/wcslog.js" strategy="afterInteractive" id="naver-wcslog" onLoad={_temp4} /><Script src="//t1.daumcdn.net/kas/static/kp.js" strategy="lazyOnload" /><Script src="https://pay.nicepay.co.kr/v1/js/" strategy="afterInteractive" id="nicepay-sdk" /><Script id="beusable-rum" strategy="lazyOnload">{"\n        (function(w, d, a){\n          w.__beusablerumclient__ = {\n            load : function(src){\n              var b = d.createElement(\"script\");\n              b.src = src; b.async=true; b.type = \"text/javascript\";\n              d.getElementsByTagName(\"head\")[0].appendChild(b);\n            }\n          };w.__beusablerumclient__.load(a + \"?url=\" + encodeURIComponent(d.URL));\n        })(window, document, \"//rum.beusable.net/load/b260408e110801u424\");\n      "}</Script><ClientScript /></>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  return t8;
};
export const GlobalController = () => {
  const $ = _c(3);
  const subDomain_0 = useDeviceInfoStore(_temp5);
  const webviewType_0 = useDeviceInfoStore(_temp6);
  if (webviewType_0 === WEBVIEW_TYPE.WELLO_APP) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <><AppController /><AppModal /></>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  switch (subDomain_0) {
    case SUB_DOMAIN.KB_PAY:
      {
        let t0;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t0 = <><ErrorBoundary fallback={null}><Suspense><KbHistoryStack /></Suspense></ErrorBoundary><KbAppController /></>;
          $[1] = t0;
        } else {
          t0 = $[1];
        }
        return t0;
      }
    default:
      {
        let t0;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t0 = <></>;
          $[2] = t0;
        } else {
          t0 = $[2];
        }
        return t0;
      }
  }
};
function _temp(t0) {
  const {
    webviewType
  } = t0;
  return webviewType;
}
function _temp2(state) {
  return [state.isLogin, state.myInfo?.memberCode];
}
function _temp3() {
  if (window.Kakao && !window.Kakao.isInitialized?.()) {
    window.Kakao.init?.(ENV.NEXT_PUBLIC_KAKAO_CLIENT_ID);
  }
}
function _temp4() {
  if (!window.wcs) {
    return;
  }
  if (!window.wcs_add) {
    window.wcs_add = {};
  }
  window.wcs_add.wa = ACCOUNT_ID;
  window.wcs.inflow(PRODUCT_DOMAIN.replace("https://", ""));
  flushNaverWcsCommands();
}
function _temp5(t0) {
  const {
    subDomain
  } = t0;
  return subDomain;
}
function _temp6(t0) {
  const {
    webviewType
  } = t0;
  return webviewType;
}